import { type JSX, type ReactNode, useState } from 'react';
import { assetConstants } from '@chainflip/utils/chainflip';
import { BigNumber } from 'bignumber.js';
import { startOfHour, subDays } from 'date-fns';
import { flip$ } from '@/shared/assets/tokens';
import { SkeletonLine } from '@/shared/components/atoms/LoadingSkeleton';
import Card from '@/shared/components/flip-ui-kit/Card';
import { useGqlQuery } from '@/shared/hooks/useGqlQuery';
import { useHistoricalPrice } from '@/shared/hooks/useHistoricalPrice';
import useTokenPrice from '@/shared/hooks/useTokenPrice';
import ChartIcon from '@/shared/icons/flip-ui-kit/large/Chart';
import CoinsIcon from '@/shared/icons/flip-ui-kit/large/Coins';
import DollarIcon from '@/shared/icons/flip-ui-kit/large/Dollar';
import FireIcon from '@/shared/icons/flip-ui-kit/large/Fire';
import GiveCoinsIcon from '@/shared/icons/flip-ui-kit/large/GiveCoins';
import LockIcon from '@/shared/icons/flip-ui-kit/large/Lock';
import SwapIcon from '@/shared/icons/flip-ui-kit/large/Swap';
import TrendingDownIcon from '@/shared/icons/flip-ui-kit/small/TrendingDown';
import TrendingUpIcon from '@/shared/icons/flip-ui-kit/small/TrendingUp';
import { FLIP_SYMBOL, chainflipAssetMap } from '@/shared/utils/env';
import { buildUrl } from '@/shared/utils/helpers';
import { formatUsdValue, formatWithCommas, formatWithNumeral } from '@/shared/utils/numbers';
import TokenAmount from '@/shared/utils/TokenAmount';
import { useFlipContract } from 'packages/block-explorer/hooks/useFlipContract';
import { getCirculatingSupplyQuery } from '../../graphql/cache/protocol';
import { getBurnInfoQuery } from '../../graphql/explorer/protocol';
import { getAllTimeSwapStatsQuery } from '../../graphql/reporting/swaps';

const CardWrapper = ({
  title,
  icon,
  footer,
  children,
  isLoading,
  headerAction,
}: {
  title: string;
  icon: JSX.Element;
  footer?: ReactNode;
  children?: ReactNode;
  isLoading?: boolean;
  headerAction?: ReactNode;
}) => (
  <Card
    className="[&>div]:justify-center [&>div]:md:justify-start"
    title={title}
    icon={icon}
    loading={isLoading}
    headerAction={headerAction}
    skeletonLineWidth={100}
    footer={
      <div className="flex justify-center gap-x-1 md:justify-start">
        {isLoading ? <SkeletonLine width={100} height={20} /> : footer}
      </div>
    }
  >
    <span className="font-aeonikMedium text-16 text-cf-light-4 md:text-24">{children}</span>
  </Card>
);

const ProtocolStatCards = () => {
  const [today] = useState<Date | null>(new Date());
  const { price } = useTokenPrice(chainflipAssetMap.Flip);
  const { totalFundedFlip, isLoading: isTotalLockedLoading } = useFlipContract();
  const { historicalPrice } = useHistoricalPrice(
    flip$,
    subDays(startOfHour(today ?? 0), 1).toISOString(),
  );

  const { data: burnInfo, isLoading: isBurnInfoLoading } = useGqlQuery(getBurnInfoQuery, {
    variables: {
      maxTimestamp: subDays(today ?? 0, 1).toISOString(),
    },
    enabled: Boolean(today),
  });

  const { data: circulation, isLoading: isCirculationLoading } = useGqlQuery(
    getCirculatingSupplyQuery,
    {
      context: { clientName: 'statechainCache' },
    },
  );

  const { data: swapStats, isLoading: isSwapStatsLoading } = useGqlQuery(getAllTimeSwapStatsQuery, {
    context: { clientName: 'reportingService' },
    variables: {
      startDate: subDays(today ?? 0, 1).toISOString(),
    },
  });

  const lockedFlipUsdValue = new BigNumber(price ?? 0)
    .times(totalFundedFlip?.toFixed(2) ?? 0)
    .toFixed(2);

  const circulatingSupply = circulation?.circulation?.circulatingSupply
    ? `${formatWithNumeral(
        new TokenAmount(
          circulation?.circulation?.circulatingSupply,
          assetConstants.Flip.decimals,
        ).toFixed(2),
      )} ${FLIP_SYMBOL}`
    : 'N/A';

  const totalSupply = formatWithNumeral(
    new TokenAmount(
      circulation?.circulation?.totalSupply || 0,
      assetConstants.Flip.decimals,
    ).toFixed(2),
  );

  const totalBurn = new TokenAmount(
    burnInfo?.currentBurn?.aggregates?.sum?.totalAmount ?? 0,
    assetConstants.Flip.decimals,
  );

  const historicalBurn = new TokenAmount(
    burnInfo?.historicalBurn?.aggregates?.sum?.totalAmount ?? 0,
    assetConstants.Flip.decimals,
  );

  const burnDelta = totalBurn.sub(historicalBurn);

  const volumeLast24Hour = BigNumber.sum(
    swapStats?.oneLegSwapsSinceStartDate?.agg?.sum?.outputValueUsd ?? 0,
    swapStats?.twoLegSwapsSinceStartDate?.agg?.sum?.intermediateValueUsd ?? 0,
    swapStats?.twoLegSwapsSinceStartDate?.agg?.sum?.outputValueUsd ?? 0,
  );

  const volume = BigNumber.sum(
    swapStats?.oneLegSwaps?.agg?.sum?.outputValueUsd ?? 0,
    swapStats?.twoLegSwaps?.agg?.sum?.intermediateValueUsd ?? 0,
    swapStats?.twoLegSwaps?.agg?.sum?.outputValueUsd ?? 0,
  );

  const historicalFlipUsd = historicalPrice?.FLIP?.usdPrice ?? 0;
  const priceDelta =
    historicalFlipUsd === 0
      ? new BigNumber(0)
      : new BigNumber(price ?? 0).minus(historicalFlipUsd).dividedBy(historicalFlipUsd).times(100);

  const countLast24Hour =
    2 * Number(swapStats?.twoLegSwapsSinceStartDate?.totalCount ?? 0) +
    Number(swapStats?.oneLegSwapsSinceStartDate?.totalCount ?? 0);

  const count =
    2 * Number(swapStats?.twoLegSwaps?.totalCount ?? 0) +
    Number(swapStats?.oneLegSwaps?.totalCount ?? 0);

  const isFlipPriceLoading = price === undefined || historicalPrice === null;

  return (
    <div className="grid grid-cols-2 gap-5 text-center md:grid-cols-3 md:text-left">
      <CardWrapper
        title="Volume / All time"
        icon={<ChartIcon className="h-[16px] w-[16px] text-cf-green-1 md:h-[unset] md:w-[unset]" />}
        isLoading={isSwapStatsLoading}
        footer={
          <>
            <span className="text-cf-light-2">Last 24H</span>
            <TrendingUpIcon className="text-cf-green-3" />
            <span className="text-cf-green-3">+{formatUsdValue(volumeLast24Hour, false)}</span>
          </>
        }
      >
        {formatUsdValue(volume, false)}
      </CardWrapper>
      <CardWrapper
        title="Swaps / All time"
        icon={<SwapIcon className="h-[16px] w-[16px] text-cf-orange-2 md:h-[unset] md:w-[unset]" />}
        isLoading={isSwapStatsLoading}
        footer={
          <>
            <span className="text-cf-light-2">Last 24H</span>
            <TrendingUpIcon className="text-cf-green-3" />
            <span className="text-cf-green-3">+{formatWithCommas(countLast24Hour)} swaps</span>
          </>
        }
      >
        {formatWithCommas(count)}
      </CardWrapper>
      <CardWrapper
        title={`Staked ${FLIP_SYMBOL}`}
        isLoading={isTotalLockedLoading}
        icon={<LockIcon className="h-[16px] w-[16px] text-cf-blue-2 md:h-[unset] md:w-[unset]" />}
        footer={<div className="text-cf-light-2">{formatUsdValue(lockedFlipUsdValue, false)}</div>}
      >
        {formatWithNumeral(totalFundedFlip?.toFixed(2) ?? 0)} {FLIP_SYMBOL}
      </CardWrapper>
      <CardWrapper
        title="FLIP Price"
        icon={
          <DollarIcon className="h-[16px] w-[16px] text-cf-green-3 md:h-[unset] md:w-[unset]" />
        }
        headerAction={
          <a
            href={buildUrl('/delegate', process.env.NEXT_PUBLIC_STAKING_URL)}
            target="_blank"
            rel="noopener noreferrer"
            className="ml-auto flex items-center gap-x-1 font-aeonikMedium text-12 text-cf-green-1 transition-colors hover:text-cf-green-2 md:text-14"
          >
            <GiveCoinsIcon className="h-[16px] w-[16px]" />
            Earn Yield on {FLIP_SYMBOL}
          </a>
        }
        isLoading={isFlipPriceLoading}
        footer={
          <>
            <span className="text-cf-light-2">Last 24H</span>
            {priceDelta.isNegative() ? (
              <TrendingDownIcon className="text-cf-red-2" />
            ) : (
              <TrendingUpIcon className="text-cf-green-3" />
            )}
            <span className={priceDelta.isNegative() ? 'text-cf-red-2' : 'text-cf-green-3'}>
              {priceDelta.toFixed(2)}%
            </span>
          </>
        }
      >
        {formatUsdValue(price) ?? ''}
      </CardWrapper>
      <CardWrapper
        title={`Total ${FLIP_SYMBOL} Burned`}
        isLoading={isBurnInfoLoading}
        icon={<FireIcon className="h-[16px] w-[16px] text-cf-red-2 md:h-[unset] md:w-[unset]" />}
        footer={
          <>
            <span className="text-cf-light-2">Last 24H</span>
            <TrendingUpIcon className="text-cf-green-3" />
            <span className="text-cf-green-3">+{burnDelta.toFixed(2)}</span>
          </>
        }
      >
        <div className="pr-0.5 font-aeonikMedium text-16 font-medium text-cf-light-4 md:text-24">
          {formatWithNumeral(totalBurn.toFixed(2))}
        </div>
      </CardWrapper>
      <CardWrapper
        title="Circulating Supply"
        icon={<CoinsIcon className="h-[16px] w-[16px] text-cf-blue-1 md:h-[unset] md:w-[unset]" />}
        isLoading={isCirculationLoading}
        footer={
          circulatingSupply !== 'N/A' && (
            <span className="text-cf-light-2">
              Out of <span className="ml-0.5 text-cf-light-3">{totalSupply}</span>
            </span>
          )
        }
      >
        {circulatingSupply}
      </CardWrapper>
    </div>
  );
};

export default ProtocolStatCards;
